import type { ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import {
  DISCIPLINE_COLORS,
  DISCIPLINE_ORDER,
  type DisciplineKey,
} from '@/lib/disciplineColors';
import type { ActivityHeatmapDay } from '@/lib/dashboard';
import { useActualsByIds, type ActualWorkout } from '@/lib/workouts';

/**
 * Panel lateral derecho (M2.17 + M2.18) que muestra el resumen del día
 * seleccionado en el `ActivityHeatmap`.
 *
 * Cambio M2.18:
 *  - El heatmap YA NO navega directo al detalle aunque haya 1 sola
 *    actividad. Siempre selecciona el día y este panel pinta:
 *      · Fecha legible + count + total min + TSS.
 *      · Desglose por disciplina (minutos).
 *      · Una card por actividad con disciplina (color + emoji),
 *        duración, distancia, avgHR, TSS y botón "Ver detalle →".
 *  - Los datos ricos por actividad vienen de `useActualsByIds(actualIds)`
 *    que pega a `GET /api/workouts/by-ids` (endpoint nuevo M2.18).
 *
 * Estados:
 *  - `day === null` → estado vacío instructivo.
 *  - `day !== null` + loading → fecha + spinner mientras carga el batch.
 *  - `day !== null` + data → render completo.
 */
export interface DaySelectionPanelProps {
  day: ActivityHeatmapDay | null;
  onClose: () => void;
}

export function DaySelectionPanel({
  day,
  onClose,
}: DaySelectionPanelProps): ReactElement {
  // Hook condicional pero estable: con day=null pasamos [] y el hook
  // queda disabled internamente (enabled: stable.length > 0).
  const ids = day?.actualIds ?? [];
  const query = useActualsByIds(ids);

  if (day === null) {
    return (
      <article
        data-testid="day-selection-panel-empty"
        aria-label="Detalle del día seleccionado"
        className="flex h-full min-h-[14rem] items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500"
      >
        <p>
          Selecciona un día en el mapa para ver el resumen aquí. Cada
          actividad tiene un botón para abrir el detalle completo.
        </p>
      </article>
    );
  }

  return (
    <article
      data-testid="day-selection-panel"
      aria-label={`Detalle del día ${formatDate(day.dateISO)}`}
      className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Resumen del día
          </p>
          <h3 className="mt-0.5 text-sm font-semibold text-slate-900">
            {formatDate(day.dateISO)}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar detalle del día"
          data-testid="day-selection-panel-close"
          className="-mr-1 -mt-1 flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          <span aria-hidden="true" className="text-base">×</span>
        </button>
      </header>

      <p className="mt-3 text-sm text-slate-700">
        <span className="font-medium tabular-nums">{day.count}</span>{' '}
        {day.count === 1 ? 'actividad' : 'actividades'} ·{' '}
        <span className="tabular-nums">{Math.round(day.totalMinutes)} min</span> ·{' '}
        <span className="tabular-nums">{Math.round(day.tss)} TSS</span>
      </p>

      <DisciplineBreakdown byDiscipline={day.byDiscipline} />

      <div className="mt-4 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Actividades
        </p>
        <div className="mt-2 space-y-2">
          {ids.length === 0 && (
            <p className="text-xs text-slate-500">
              No hay actividades registradas este día.
            </p>
          )}
          {ids.length > 0 && query.isLoading && (
            <p
              role="status"
              aria-live="polite"
              className="text-xs text-slate-500"
            >
              Cargando actividades…
            </p>
          )}
          {ids.length > 0 && query.isError && (
            <p role="alert" className="text-xs text-rose-700">
              No se pudo cargar el detalle de las actividades.
            </p>
          )}
          {ids.length > 0 && query.data && (
            <>
              {query.data.items.length === 0 ? (
                <p className="text-xs text-slate-500">
                  Las actividades de este día no están disponibles.
                </p>
              ) : (
                query.data.items.map((a) => (
                  <ActivityCard key={a.id} actual={a} />
                ))
              )}
            </>
          )}
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Card de una actividad
// ---------------------------------------------------------------------------

const DISCIPLINE_EMOJI: Record<DisciplineKey, string> = {
  swim: '🏊',
  bike: '🚴',
  run: '🏃',
  strength: '🏋️',
  brick: '🔁',
  other: '🏅',
};

function ActivityCard({ actual }: { actual: ActualWorkout }): ReactElement {
  const disc = actual.discipline as DisciplineKey;
  const palette = DISCIPLINE_COLORS[disc];
  const emoji = DISCIPLINE_EMOJI[disc] ?? '🏅';

  return (
    <article
      data-testid={`day-selection-activity-${actual.id}`}
      className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
      style={{ borderLeft: `4px solid ${palette.hex}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
            <span aria-hidden="true">{emoji}</span>
            <span>{palette.label}</span>
          </p>
          <p className="mt-1 text-xs text-slate-500 tabular-nums">
            {formatTimeShort(actual.datetime, actual.timezone)}
          </p>
        </div>
        <Link
          to="/entreno/$id"
          params={{ id: String(actual.id) }}
          data-testid={`day-selection-link-${actual.id}`}
          className="inline-flex shrink-0 items-center gap-1 rounded border border-teal-300 bg-teal-50 px-2 py-1 text-xs font-medium text-teal-800 hover:bg-teal-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          Ver detalle <span aria-hidden="true">→</span>
        </Link>
      </div>

      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <Stat label="Duración" value={`${Math.round(actual.durationMin)} min`} />
        {actual.distanceM !== null && actual.distanceM > 0 && (
          <Stat
            label="Distancia"
            value={formatDistance(actual.distanceM, disc)}
          />
        )}
        {actual.avgHr !== null && actual.avgHr !== undefined && (
          <Stat label="HR med." value={`${actual.avgHr} bpm`} />
        )}
        {actual.avgPower !== null && actual.avgPower !== undefined && (
          <Stat label="Pot. med." value={`${actual.avgPower} W`} />
        )}
        {(actual.tssComputed ?? actual.tssEstimated) !== null &&
          (actual.tssComputed ?? actual.tssEstimated) !== undefined && (
            <Stat
              label="TSS"
              value={String(
                Math.round((actual.tssComputed ?? actual.tssEstimated)!),
              )}
            />
          )}
      </dl>
    </article>
  );
}

function Stat({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium tabular-nums text-slate-900">
        {value}
      </dd>
    </>
  );
}

// ---------------------------------------------------------------------------
// Desglose por disciplina (minutos)
// ---------------------------------------------------------------------------

function DisciplineBreakdown({
  byDiscipline,
}: {
  byDiscipline: ActivityHeatmapDay['byDiscipline'];
}): ReactElement {
  const rows = DISCIPLINE_ORDER.filter((d) => (byDiscipline[d] ?? 0) > 0).map(
    (d) => ({ discipline: d, mins: byDiscipline[d] ?? 0 }),
  );
  if (rows.length === 0) {
    return (
      <p className="mt-3 text-xs text-slate-500">Sin desglose por disciplina.</p>
    );
  }
  return (
    <ul className="mt-3 space-y-1">
      {rows.map((row) => (
        <li
          key={row.discipline}
          className="flex items-center justify-between gap-3 text-xs"
        >
          <span className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: DISCIPLINE_COLORS[row.discipline].hex }}
            />
            <span className="text-slate-700">
              {DISCIPLINE_COLORS[row.discipline].label}
            </span>
          </span>
          <span className="tabular-nums text-slate-900">
            {Math.round(row.mins)} min
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${iso}T00:00:00.000Z`));
  } catch {
    return iso;
  }
}

function formatTimeShort(iso: string, tz: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz || 'UTC',
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

function formatDistance(distanceM: number, discipline: DisciplineKey): string {
  // Natación: muchas piscinas son <2km → usar metros.
  if (discipline === 'swim' && distanceM < 2000) {
    return `${Math.round(distanceM)} m`;
  }
  const km = distanceM / 1000;
  return `${km.toFixed(km < 10 ? 2 : 1)} km`;
}
